/**
 * Experts Slider JavaScript
 * Simple, working slider for all screen sizes
 */

class ExpertsSlider {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.experts-grid');
    this.slides = Array.from(container.querySelectorAll('.expert-card'));
    this.currentIndex = 0;
    this.isTransitioning = false;
    
    if (this.slides.length === 0) return;
    
    this.init();
  }
  
  init() {
    console.log('Experts Slider: Found', this.slides.length, 'cards');
    this.setupSlider();
    this.createControls();
    this.setupEventListeners();
    this.updateSlider();
  }
  
  setupSlider() {
    // Wrap slides in a track if not already wrapped
    this.track.classList.add('experts-slider-track');
    
    // Set initial state
    this.getSlidesPerView();
  }
  
  getSlidesPerView() {
    const width = window.innerWidth;
    
    if (width >= 1024) {
      this.slidesPerView = 3;
    } else if (width >= 768) {
      this.slidesPerView = 2;
    } else {
      this.slidesPerView = 1;
    }
    
    return this.slidesPerView;
  }
  
  createControls() {
    // Create dots
    this.dotsContainer = document.createElement('div');
    this.dotsContainer.className = 'experts-slider-dots';
    
    const totalDots = this.slides.length - this.slidesPerView + 1;
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.className = 'experts-slider-dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      if (i === 0) dot.classList.add('active');
      dot.addEventListener('click', () => this.goToSlide(i));
      this.dotsContainer.appendChild(dot);
    }
    
    this.container.appendChild(this.dotsContainer);
    
    // Create prev button
    this.prevBtn = document.createElement('button');
    this.prevBtn.className = 'experts-slider-btn experts-slider-btn--prev';
    this.prevBtn.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    this.prevBtn.addEventListener('click', () => this.prev());
    
    // Create next button
    this.nextBtn = document.createElement('button');
    this.nextBtn.className = 'experts-slider-btn experts-slider-btn--next';
    this.nextBtn.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    this.nextBtn.addEventListener('click', () => this.next());
    
    this.track.appendChild(this.prevBtn);
    this.track.appendChild(this.nextBtn);
  }
  
  updateControls() {
    // Update dots
    const dots = this.dotsContainer.querySelectorAll('.experts-slider-dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentIndex);
    });
    
    // Update button states
    this.prevBtn.disabled = this.currentIndex === 0;
    this.nextBtn.disabled = this.currentIndex >= this.slides.length - this.slidesPerView;
  }
  
  goToSlide(index) {
    if (this.isTransitioning) return;
    
    const maxIndex = this.slides.length - this.slidesPerView;
    this.currentIndex = Math.max(0, Math.min(index, maxIndex));
    this.updateSlider();
  }
  
  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSlider();
    }
  }
  
  next() {
    const maxIndex = this.slides.length - this.slidesPerView;
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.updateSlider();
    }
  }
  
  updateSlider() {
    this.isTransitioning = true;
    
    // Calculate slide width based on slides per view
    const slideWidth = 100 / this.slidesPerView;
    
    // Set each slide width
    this.slides.forEach(slide => {
      slide.style.width = `${slideWidth}%`;
      slide.style.flex = `0 0 ${slideWidth}%`;
    });
    
    // Calculate transform
    const translateX = -(this.currentIndex * slideWidth);
    this.track.style.transform = `translateX(${translateX}%)`;
    
    console.log('Slider update:', {
      currentIndex: this.currentIndex,
      slidesPerView: this.slidesPerView,
      slideWidth: slideWidth,
      translateX: translateX
    });
    
    this.updateControls();
    
    setTimeout(() => {
      this.isTransitioning = false;
    }, 400);
  }
  
  setupEventListeners() {
    // Touch events
    let touchStartX = 0;
    let touchEndX = 0;
    
    this.track.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.track.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
    
    // Keyboard navigation
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });
    
    // Window resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const oldSlidesPerView = this.slidesPerView;
        this.getSlidesPerView();
        
        if (oldSlidesPerView !== this.slidesPerView) {
          this.currentIndex = 0;
          this.updateDots();
          this.updateSlider();
        } else {
          this.updateSlider();
        }
      }, 250);
    });
  }
  
  updateDots() {
    // Remove old dots
    this.dotsContainer.innerHTML = '';
    
    // Create new dots
    const totalDots = this.slides.length - this.slidesPerView + 1;
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.className = 'experts-slider-dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      if (i === this.currentIndex) dot.classList.add('active');
      dot.addEventListener('click', () => this.goToSlide(i));
      this.dotsContainer.appendChild(dot);
    }
  }
  
  handleSwipe(startX, endX) {
    const threshold = 50;
    const diff = startX - endX;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.section-experts');
  containers.forEach(container => {
    new ExpertsSlider(container);
  });
});

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.section-experts');
    if (container) {
      new ExpertsSlider(container);
    }
  });
}
